import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {ArrUtil} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Items} from '@api/model/items'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {Company} from '@extensions/company'
import {globals} from '@extensions/globals-base'
import {UserAccess} from '@extensions/user-access'
import {swapArrayLocs} from './utils'

interface State {
  all: Company[]
  edited: DataObjectId
  showConfigure: boolean
  adding: boolean
  draggedCompany: DataObjectId
  companyName: string
  mouseEnter: boolean
}

export class CompaniesStore {
  state: State = {
    all: Company.orderedList(),
    edited: null,
    // only for users older than 30 jun 2020
    showConfigure:
      Sync.getProfile()?.get(Sync.profileId)?.timestamp < 1593464400000,
    adding: false,
    draggedCompany: null,
    companyName: '',
    mouseEnter: false
  }

  constructor() {
    makeAutoObservable(this, {
      state: observable.shallow
    })
  }

  setCompanyName(name: string): void {
    this.state.companyName = name
  }

  configure(): void {
    const isAdmin = PermissionsChecker.isAdmin()
    if (isAdmin) {
      globals.scriptsOpen = true
      globals.scriptsOpenUpdated?.()
    } else {
      MessageBox.warn(Lang.msg.notAdminConfigureWarn)
    }
  }

  editOpen(): void {
    this.state.adding = true
  }

  editSubmit(): void {
    const box = Sync.getCurrentSandbox()
    const text = this.state.companyName
    if (!text) {
      this.editCancel()
      return
    }
    const isAdmin = Sync.get({
      companyId: this.state.edited
    }).users.get(globals.currentUserId).isAdmin
    if (!isAdmin) {
      MessageBox.show(Lang.msg.disallowRenameCompanyNotAdmin, 'danger')
      return
    }
    if (!Items.rename(Items.get(box.companyId), text)) {
      this.editCancel()
      return
    }
    const b = Sync.get({companyId: this.state.edited})
    b.editTitle(b.get(b.companyId) as Hub, text)
    this.state.all = Company.orderedList()
    this.state.edited = undefined
  }

  editCancel(): boolean {
    this.state.edited = undefined
    return true
  }

  edit(company: Company): void {
    const isAdmin = Sync.get({companyId: company.id}).users.get(
      globals.currentUserId
    ).isAdmin
    if (!isAdmin) {
      MessageBox.show(Lang.msg.disallowRenameCompanyNotAdmin, 'danger')
      return
    }
    this.state.edited = company.id
  }

  async delete(company: Company): Promise<void> {
    if (Company.orderedList().length < 2) {
      MessageBox.show(Lang.msg.disallowDeleteOnlyCompany, 'danger')
      return
    }
    if (
      await showDeleteConfirm({
        title: Lang.msg.confirmDeleteCompany3,
        subtitle: Lang.msg.confirmDeleteCompany3Hint
      })
    ) {
      const b = Sync.get({companyId: company.id})
      UserAccess.removeUserFromCompany(globals.currentUserId, b)
      MessageBox.show(Lang.msg.companyDeleted, 'success')
    }
  }

  addingSubmit(name: string): void {
    this.state.adding = false
    if (!name) {
      return
    }
    void Company.create(name)
  }

  addingCancel(): boolean {
    this.state.adding = false
    return true
  }

  setCompany(companyId: DataObjectId): void {
    Company.setCurrent(companyId)
  }

  dragStart(companyId: DataObjectId, e: React.DragEvent): void {
    DragData.set(e, {type: 'company', id: companyId})
    const user = Sync.getCurrentUser()
    this.state.draggedCompany = companyId

    if (!user.companyOrder || user.companyOrder.length === 0) {
      user.companyOrder = Company.orderedList().map(c => c.id)
    }
    Company.orderedList().forEach(c => {
      if (!user.companyOrder.includes(c.id)) {
        user.companyOrder.push(c.id)
      }
    })
  }

  dragEnd(): void {
    const user = Sync.getCurrentUser()
    user.commit()
    this.state.draggedCompany = undefined
  }

  dragEnter(companyId: DataObjectId): void {
    const data = DragData.get()
    if (data.type === 'company') {
      const user = Sync.getCurrentUser()
      const order = user.companyOrder
      const ixDrag = order.indexOf(data.id)
      const ixOver = order.indexOf(companyId)
      swapArrayLocs(order, ixDrag, ixOver)
      this.state.all = ArrUtil.defined(order.map(Company.get))
    }
  }

  dragChange(oldOne: number, newOne: number): void {
    const user = Sync.getCurrentUser()
    const order = user.companyOrder
    swapArrayLocs(order, oldOne, newOne)
    user.commit()
  }

  recalculateList(): void {
    this.state.all = Company.orderedList()
  }

  setMouseEnter(value: boolean) {
    this.state.mouseEnter = value
  }
}

const [CompaniesContext, useCompaniesStore] = createUseStore(CompaniesStore)
export {CompaniesContext, useCompaniesStore}
